import {
  useLayoutEffect,
  useRef,
  useState,
  type TransitionEvent,
} from "react";

/** Must mirror `--cash-stores-reel-duration` in CSS. */
const REEL_SPIN_MS = 1463;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Absolute translate from track origin so the target slot centers in the viewport. */
function measureReelTranslateFromLayout(
  viewport: HTMLElement,
  target: HTMLElement,
): number {
  return (
    viewport.clientWidth / 2 - (target.offsetLeft + target.offsetWidth / 2)
  );
}

/** Delta to apply on top of the current track translate (uses visual bounds). */
function measureReelCenterCorrection(
  viewport: HTMLElement,
  target: HTMLElement,
): number {
  const viewportRect = viewport.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const viewportCenter = viewportRect.left + viewportRect.width / 2;
  const targetCenter = targetRect.left + targetRect.width / 2;
  return viewportCenter - targetCenter;
}

function snapTrackTranslate(
  track: HTMLElement,
  apply: (next: number | ((prev: number) => number)) => void,
  correction: number,
) {
  if (Math.abs(correction) <= 0.5) return;
  track.style.transition = "none";
  apply((prev) => prev + correction);
  requestAnimationFrame(() => {
    track.style.transition = "";
  });
}

export function useCashStoresReelCenter(
  targetIndex: number,
  reelLength: number,
) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  const spunForTarget = useRef(-1);
  const reelPhaseRef = useRef<"spin" | "landed">("spin");
  const landTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [translate, setTranslate] = useState(0);
  const [reelPhase, setReelPhase] = useState<"spin" | "landed">("spin");

  reelPhaseRef.current = reelPhase;

  useLayoutEffect(() => {
    if (targetIndex < 0 || reelLength === 0) return;
    if (spunForTarget.current === targetIndex) return;

    const viewport = viewportRef.current;
    const target = targetRef.current;
    if (!viewport || !target) return;

    spunForTarget.current = targetIndex;
    setReelPhase("spin");

    const finalTranslate = measureReelTranslateFromLayout(viewport, target);

    if (prefersReducedMotion() || Math.abs(finalTranslate) < 1) {
      setTranslate(finalTranslate);
      setReelPhase("landed");
      return;
    }

    requestAnimationFrame(() =>
      requestAnimationFrame(() => setTranslate(finalTranslate)),
    );
    landTimer.current = setTimeout(
      () => setReelPhase("landed"),
      REEL_SPIN_MS + 200,
    );
  }, [targetIndex, reelLength]);

  useLayoutEffect(() => {
    if (reelPhase !== "landed" || targetIndex < 0) return;

    const viewport = viewportRef.current;
    const target = targetRef.current;
    const track = trackRef.current;
    if (!viewport || !target || !track) return;

    snapTrackTranslate(
      track,
      setTranslate,
      measureReelCenterCorrection(viewport, target),
    );
  }, [reelPhase, targetIndex]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || targetIndex < 0) return;

    const observer = new ResizeObserver(() => {
      if (reelPhaseRef.current !== "landed") return;
      const target = targetRef.current;
      const track = trackRef.current;
      if (!target || !track) return;
      snapTrackTranslate(
        track,
        setTranslate,
        measureReelCenterCorrection(viewport, target),
      );
    });

    observer.observe(viewport);
    return () => observer.disconnect();
  }, [targetIndex]);

  useLayoutEffect(
    () => () => {
      clearTimeout(landTimer.current);
    },
    [],
  );

  const handleReelEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (event.propertyName !== "transform") return;
    clearTimeout(landTimer.current);
    setReelPhase("landed");
  };

  return {
    viewportRef,
    trackRef,
    targetRef,
    translate,
    reelPhase,
    handleReelEnd,
  };
}
